import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { ErrorState, LoadingState } from '@/components/admin';
import { SessionNoteEditor } from '@/features/sessions/components/SessionNoteEditor';
import { useSession, useSessionNote } from '@/features/sessions/hooks';
import { requirePermission } from '@/lib/guards';
import { surfacePaths } from '@/lib/paths';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/app/sessions/$sessionId/note')({
  beforeLoad: () => {
    requirePermission('session_note:create');
  },
  component: ClinicalSessionNoteScreen,
  // Editor page owns the full canvas — no shell padding around the card.
  staticData: { fullBleed: true },
});

export function ClinicalSessionNoteScreen() {
  const { sessionId } = useParams({ from: '/app/sessions/$sessionId/note' });
  const navigate = useNavigate();
  const sessionQ = useSession(sessionId);
  const noteQuery = useSessionNote(sessionId);
  const userFirstName = useAuthStore((s) => s.user?.firstName ?? 'you');
  const paths = surfacePaths('clinical');

  if (sessionQ.isLoading || noteQuery.isLoading) return <LoadingState variant="form" />;
  if (sessionQ.error || noteQuery.error) {
    const err = noteQuery.error ?? sessionQ.error;
    return (
      <ErrorState
        description={err instanceof Error ? err.message : undefined}
        onRetry={() => {
          void noteQuery.refetch();
          void sessionQ.refetch();
        }}
      />
    );
  }
  if (sessionQ.data === undefined) return <p className="p-6">Session not found.</p>;

  const session = sessionQ.data;
  // From the session, not GET /clients/:id: that read is cadence-gated, and a note
  // must stay writable for a psychologist who is behind on availability.
  const clientName = session.clientName ?? 'Client';

  const returnToSession = (): void => {
    void navigate({ to: paths.sessions.detail(sessionId) });
  };

  // Full-bleed by spec: the new ClinicalEditorShell paints its own sticky
  // topbar / footer chrome and a side ribbon; no outer max-width wrapper or
  // JournalPageHeader needed. StatusTimeline likewise lives off this surface
  // now (the topbar carries kind label, status badge and back link).
  return (
    <SessionNoteEditor
      sessionId={sessionId}
      practitionerId={session.practitionerId}
      clientId={session.clientId}
      clientName={clientName}
      scheduledStart={session.scheduledStart}
      authorName={userFirstName}
      note={noteQuery.data ?? null}
      onSubmitted={returnToSession}
    />
  );
}
